import { isEqual } from 'es-toolkit';
import { useLocalStorage } from 'minimal-shared/hooks';
import { getStorage as getStorageValue } from 'minimal-shared/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SETTINGS_STORAGE_KEY } from '../settings-config';
import type { SettingsProviderProps, SettingsState } from '../types';
import { SettingsContext } from './settings-context';

/**
 * Provider component for managing application settings with local storage persistence.
 *
 * This provider handles:
 * - Storing and retrieving settings from local storage
 * - Managing settings drawer open/close state
 * - Resetting settings to default values
 * - Version checking to reset outdated settings automatically
 *
 * @param props - The props for the SettingsProvider component
 * @param props.children - React child components to render within the provider
 * @param props.defaultSettings - Default settings to use when initializing or resetting
 * @param props.storageKey - Key used for local storage (defaults to SETTINGS_STORAGE_KEY)
 *
 * @returns A context provider that makes settings available to child components
 *
 * @example
 * ```tsx
 * <SettingsProvider defaultSettings={initialSettings}>
 *   <App />
 * </SettingsProvider>
 * ```
 */
export function SettingsProvider({
    children,
    defaultSettings,
    storageKey = SETTINGS_STORAGE_KEY,
}: SettingsProviderProps) {
    const { state, setState, resetState, setField } = useLocalStorage<SettingsState>(
        storageKey,
        defaultSettings,
    );

    const [openDrawer, setOpenDrawer] = useState(false);

    const onToggleDrawer = useCallback(() => {
        setOpenDrawer((prev) => !prev);
    }, []);

    const onCloseDrawer = useCallback(() => {
        setOpenDrawer(false);
    }, []);

    const canReset = !isEqual(state, defaultSettings);

    const onReset = useCallback(() => {
        resetState(defaultSettings);
    }, [defaultSettings, resetState]);

    // Version check and reset handling
    useEffect(() => {
        const storedValue = getStorageValue<SettingsState>(storageKey);

        if (storedValue) {
            try {
                if (!storedValue.version || storedValue.version !== defaultSettings.version) {
                    onReset();
                }
            } catch {
                onReset();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const memoizedValue = useMemo(
        () => ({
            canReset,
            onReset,
            openDrawer,
            onCloseDrawer,
            onToggleDrawer,
            state,
            setState,
            setField,
        }),
        [canReset, onReset, openDrawer, onCloseDrawer, onToggleDrawer, state, setField, setState],
    );

    return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}
